import React, { useState } from 'react';
import { api } from '@/lib/api';
import { getInitials, resolveMediaUrl } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Trophy } from 'lucide-react';
import { toast } from 'sonner';

function PlayerIdentity({ player }) {
  return (
    <span className="flex min-w-0 items-center gap-3">
      <Avatar className="h-12 w-12 shrink-0">
        <AvatarImage src={resolveMediaUrl(player.picture)} alt={player.name} />
        <AvatarFallback>{getInitials(player.name)}</AvatarFallback>
      </Avatar>
      <span className="min-w-0 break-words">
        <span className="block font-semibold">{player.name}</span>
        {player.nickname && <span className="block text-sm italic text-primary">«{player.nickname}»</span>}
      </span>
    </span>
  );
}

export function MatchMvp({ matchId, mvp, participants, onVote, viewerId }) {
  const [selected, setSelected] = useState('');
  const [saving, setSaving] = useState(false);
  if (!mvp?.has_started && !mvp?.is_open && !mvp?.is_closed) return null;
  const candidates = participants.filter(p => p.status === 'confirmed');
  const users = new Map(candidates.map(p => [p.user_id, p.user]));
  const ranking = (mvp.ranking || candidates.map(p => ({
    user_id: p.user_id, votes: mvp.counts?.[p.user_id] || 0,
  }))).map(player => {
    const user = users.get(player.user_id);
    return {
      ...player,
      name: user?.name || player.name || 'Giocatore',
      nickname: user ? user.nickname : player.nickname,
      picture: user ? user.picture : player.picture,
    };
  }).sort((a, b) => b.votes - a.votes || a.name.localeCompare(b.name, 'it'));
  const winners = ranking.filter(p => mvp.winners?.includes(p.user_id));
  const myChoice = ranking.find(p => p.user_id === mvp.my_vote);
  const canVote = mvp.is_open && mvp.can_vote && !mvp.my_vote && candidates.length > 0;
  const remaining = Math.max(0, mvp.eligible_voters - mvp.total_votes);
  const vote = async () => {
    if (!canVote || saving || !users.has(selected) || selected === viewerId) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/matches/${matchId}/mvp`, { candidate_id: selected });
      onVote(data);
      toast.success('Voto MVP registrato');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Voto non salvato. Riprova.');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Card id="mvp" className="border-primary/40 bg-primary/5 scroll-mt-24" data-testid="match-mvp">
      <CardContent className="p-5 space-y-4">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2"><Trophy className="h-5 w-5 text-primary" /> {mvp.is_closed ? (winners.length > 1 ? 'MVP a pari merito' : winners.length ? 'MVP eletto' : 'Votazione conclusa') : 'Vota il migliore in campo'}</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {mvp.is_closed ? 'Risultato definitivo della votazione.' : 'Si elegge l’MVP quando tutti i presenti hanno votato o, al più tardi, 24 ore dopo la fine della partita. I guest non contano tra i votanti.'}
          </p>
        </div>
        {mvp.is_closed && (
          <div className="rounded-lg bg-background p-4 border border-primary/30" role="status" data-testid="mvp-winner">
            {winners.length ? winners.map(player => (
              <div key={player.user_id} className="flex items-center justify-between gap-3 py-1">
                <PlayerIdentity player={player} />
                <span className="shrink-0 text-sm font-semibold">{player.votes} {player.votes === 1 ? 'voto' : 'voti'}</span>
              </div>
            )) : <p>Nessun MVP eletto: non sono stati espressi voti.</p>}
          </div>
        )}
        {mvp.my_vote ? (
          <div role="status" className="space-y-2">
            <p className="text-sm">Il tuo voto MVP</p>
            {myChoice ? <PlayerIdentity player={myChoice} /> : <p>Voto registrato. Grazie!</p>}
          </div>
        ) : canVote ? (
          <p id="mvp-choice-label" className="text-sm font-medium">Seleziona la scheda del giocatore, poi conferma il voto.</p>
        ) : !mvp.is_closed && <p className="text-sm text-muted-foreground">{candidates.length ? 'Votano i presenti alla partita con un account, esclusi i guest.' : 'Nessun partecipante confermato da votare.'}</p>}
        <div className="border-t pt-3 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-semibold">{mvp.is_closed ? 'Voti finali' : 'Voti in corso'}</h3>
            <p className="text-sm text-muted-foreground">{mvp.total_votes} {mvp.total_votes === 1 ? 'voto ricevuto' : 'voti ricevuti'}{mvp.eligible_voters != null && ` su ${mvp.eligible_voters}`}</p>
          </div>
          <ol aria-label="Classifica voti MVP" className="space-y-2">
            {ranking.map(player => (
              <li key={player.user_id} className={`rounded-lg border bg-background text-sm ${canVote && selected === player.user_id ? 'border-primary ring-1 ring-primary' : 'border-transparent'}`}>
                <label className={`flex items-center justify-between gap-3 p-3 ${canVote ? 'cursor-pointer' : ''}`}>
                  {canVote && player.user_id !== viewerId && <input type="radio" name="mvp-player" form="mvp-vote" value={player.user_id} checked={selected === player.user_id} onChange={() => setSelected(player.user_id)} disabled={saving || !users.has(player.user_id)} required className="h-4 w-4 shrink-0 accent-primary" />}
                  <span className="min-w-0 flex-1 flex items-center gap-2"><PlayerIdentity player={player} />{player.user_id === viewerId && <span className="text-xs text-muted-foreground">(tu)</span>}</span>
                  <span className="shrink-0 text-right font-semibold tabular-nums">
                    {player.votes} {player.votes === 1 ? 'voto' : 'voti'}
                    {mvp.is_closed && mvp.winners?.includes(player.user_id) && <span className="block text-primary">MVP</span>}
                  </span>
                </label>
              </li>
            ))}
          </ol>
          {canVote && (
            <form id="mvp-vote" aria-labelledby="mvp-choice-label" onSubmit={e => { e.preventDefault(); vote(); }} className="space-y-2">
              <Button type="submit" disabled={!users.has(selected) || saving} className="w-full">{saving ? 'Invio…' : 'Conferma voto MVP'}</Button>
              <p className="text-xs text-muted-foreground">Il voto confermato non si può modificare.</p>
            </form>
          )}
          {!mvp.is_closed && mvp.eligible_voters != null && <p className="text-xs text-muted-foreground">{remaining === 1 ? 'Manca 1 voto' : `Mancano ${remaining} voti`} per eleggere l’MVP{mvp.closes_at && `, altrimenti si chiude ${new Date(mvp.closes_at).toLocaleString('it-IT', { weekday: 'long', hour: '2-digit', minute: '2-digit' })}`}.</p>}
        </div>
      </CardContent>
    </Card>
  );
}
